import type { ComponentPropsWithoutRef, ElementRef } from 'react'
import { forwardRef } from 'react'
import * as PopoverPrimitive from '@radix-ui/react-popover'
import { cn } from '@jotta/utils/css'
import { PopoverArrow } from '@radix-ui/react-popover'

const Popover = PopoverPrimitive.Root

const PopoverTrigger = PopoverPrimitive.Trigger

const PopoverAnchor = PopoverPrimitive.Anchor

type PopoverContentProps = ComponentPropsWithoutRef<
  typeof PopoverPrimitive.Content
> & { hasArrow?: Boolean }

const PopoverContent = forwardRef<
  ElementRef<typeof PopoverPrimitive.Content>,
  PopoverContentProps
>(({ className, hasArrow = true, ...props }, ref) => {
  const bgColor = className?.split(' ').find(cls => cls.startsWith('bg-'))

  return (
    <PopoverPrimitive.Portal>
      <PopoverPrimitive.Content
        ref={ref}
        align="center"
        sideOffset={hasArrow ? 0 : 10}
        collisionPadding={10}
        className="origin-[--radix-popover-content-transform-origin] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2"
        {...props}
      >
        {hasArrow && (
          <PopoverArrow
            style={{
              borderRadius: '0 3px',
            }}
            className="h-3 w-3 translate-y-[-6px] rotate-[-45deg] rounded-sm bg-primary fill-transparent drop-shadow-[-1px_1px_2px_rgba(0,0,0,0.15)]"
          />
        )}
        <div
          className={cn(
            'max-h-[40vh] max-w-[90vw] overflow-auto rounded-md bg-primary outline-none drop-shadow-[0_1px_4px_rgba(0,0,0,0.25)]',
            className,
          )}
        >
          {props.children}
        </div>
        {hasArrow && (
          <PopoverArrow
            // Another arrow in front to overlay the content shadow
            style={{
              borderRadius: '0 3px',
              clipPath: 'polygon(0 100%, 0 0, 100% 100%)',
            }}
            className={cn(
              'h-3 w-3 translate-y-[-6px] rotate-[-45deg] rounded-sm bg-primary fill-transparent',
              bgColor,
            )}
          />
        )}
      </PopoverPrimitive.Content>
    </PopoverPrimitive.Portal>
  )
})
PopoverContent.displayName = PopoverPrimitive.Content.displayName

export { Popover, PopoverTrigger, PopoverContent, PopoverAnchor }
export type { PopoverContentProps }
